import { useChat } from "@ai-sdk/react";
import { useObserve, useValue } from "@legendapp/state/react";
import { nanoid } from "@repo/shared/utils/id";
import { Button } from "@repo/ui/button";
import { cn } from "@repo/ui/lib/utils";
import type { FileUIPart, UIMessage } from "ai";
import { useCallback, useEffect, useRef, useState } from "react";

import { logger } from "~/libs/logger";
import { trpc } from "~/libs/trpc.lib";
import { chat$ } from "~/stores/chat";
import {
  activeThreadId$,
  newThread,
  refreshThreadTitle,
  threads$,
} from "~/stores/threads";
import { getChatContext } from "./chat-context";
import { registerStop } from "./chat-controller";
import { ChatHeader } from "./chat-header";
import { ChatInput } from "./chat-input";
import type { ChatInputEditorHandle } from "./chat-input-editor";
import { ChatMessages, ChatMessagesSkeleton } from "./chat-messages";
import {
  CONTINUATION_PROMPT,
  MAX_AUTO_CONTINUATIONS,
  STEP_LIMIT_NOTICE,
  transport,
} from "./constants";
import { GeneratingThreadView } from "./generating-thread-view";
import { getCachedMessages, setCachedMessages } from "./message-cache";
import { SuggestedPrompts } from "./suggested-prompts";
import type {
  PendingImage,
  ToolCallApprovalData,
  ToolResumeData,
} from "./types";
import { useChatImages } from "./use-chat-images";

/**
 * A thread is only treated as live-tailable for this long after creation.
 * Onboarding threads keep their `{ onboarding: true }` metadata forever, so
 * this bounds the "generating" view to the window right after onboarding —
 * older threads always render their recalled transcript directly.
 */
const GENERATING_RECENCY_MS = 30 * 60 * 1000;

const isRecent = (createdAt: unknown): boolean => {
  if (!createdAt) return false;
  const ts = new Date(createdAt as string | number | Date).getTime();
  return Number.isFinite(ts) && Date.now() - ts < GENERATING_RECENCY_MS;
};

type DraftState = { text: string; mentionCount: number };
const EMPTY_DRAFT: DraftState = { text: "", mentionCount: 0 };

/**
 * Builds a synthetic assistant message carrying a single `data-error` or
 * `data-notice` part. Used to surface stream failures, aborts, and empty
 * completions inline in the transcript. These are session-only — Mastra owns
 * persistence and does not write them back, so they clear on reload.
 */
const makeSyntheticMessage = (
  partType: "data-error" | "data-notice",
  message: string,
): UIMessage => ({
  id: nanoid(),
  role: "assistant",
  parts: [{ type: partType, id: nanoid(), data: { message } }],
});

/**
 * True when the just-finished turn's last assistant message has no visible
 * prose and nothing else that explains the stop: no text, no error/notice
 * part, and no tool still awaiting the user (a suspend/approval, which means
 * the turn paused rather than finished empty). These turns would otherwise
 * end with only tool/reasoning rows and no explanation.
 */
/**
 * Reads the top-level LLM finish reason the server copied onto the last
 * assistant message's metadata (via the route's `messageMetadata` callback).
 * `"tool-calls"` means the step budget was exhausted mid tool-loop and the
 * turn can be auto-continued; other abnormal reasons already carry a
 * server-injected notice.
 */
const getFinishReason = (messages: UIMessage[]): string | undefined => {
  const last = messages.at(-1);
  if (last?.role !== "assistant") return undefined;
  const meta = last.metadata as { finishReason?: string } | undefined;
  return meta?.finishReason;
};

const isEmptyCompletion = (messages: UIMessage[]): boolean => {
  const last = messages.at(-1);
  if (last?.role !== "assistant") return false;

  let hasText = false;
  for (const part of last.parts) {
    if (part.type === "text" && part.text.trim().length > 0) {
      hasText = true;
    }
    if (part.type === "data-error" || part.type === "data-notice") {
      return false;
    }
    if (
      typeof part.type === "string" &&
      part.type.startsWith("tool-") &&
      "state" in part &&
      part.state !== "output-available" &&
      part.state !== "output-error"
    ) {
      // A tool paused for the user (askUser / approval) — not an empty finish.
      return false;
    }
  }
  return !hasText;
};

/**
 * `pane` (default) renders the chat docked in the right side pane with its own
 * {@link ChatHeader}. `page` renders the chat for the full-page `/thread/$id`
 * route: the internal header is omitted (the page supplies its own
 * `Content.Header`) and the conversation + input are constrained to a centered,
 * ChatGPT-style reading column.
 */
export type ChatLayout = "pane" | "page";

export const Chat = ({
  layout = "pane",
  threadId,
}: {
  layout?: ChatLayout;
  threadId?: string;
} = {}) => {
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
  const editorRef = useRef<ChatInputEditorHandle | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const images = useChatImages();

  // Counts how many times the current turn has auto-continued after hitting
  // the step budget. Reset to 0 on every real user-initiated turn (see
  // `sendWithContext` / `handleRetry`); the auto-continuation in `onFinish`
  // only increments it, so a turn is bounded at `MAX_AUTO_CONTINUATIONS`.
  const continuationCountRef = useRef(0);

  const {
    messages,
    sendMessage,
    setMessages,
    status,
    error,
    clearError,
    stop,
    regenerate,
  } = useChat({
    transport,
    // Seed from the shared transcript cache so a freshly mounted instance
    // (pane <-> full page, or a sidebar thread open) paints fully populated on
    // its first render — the frame the View Transition snapshots — instead of
    // flashing empty until the async hydrate lands.
    messages: getCachedMessages(threadId ?? activeThreadId$.get() ?? "") ?? [],
    // Mastra is the single writer for the transcript: the server persists every
    // turn (including post-tool assistant text) as the stream finalizes, so the
    // client no longer re-saves anything. `onFinish` only handles session-local
    // concerns — auto-continuation, in-session notices, the shared paint cache,
    // and refreshing the thread so a freshly generated title populates without a
    // reload even if the Ably broadcast was missed.
    onFinish: ({ messages: finalMessages, isAbort, isError }) => {
      // Aborts are partial, so we neither re-sync nor poll for a title.
      if (isAbort) return;
      const threadId = activeThreadId$.get();
      if (!threadId) return;

      // A turn that ends on `finishReason: "tool-calls"` exhausted the
      // supervisor's step budget mid tool-loop. Rather than surface a notice,
      // silently continue the turn (up to MAX_AUTO_CONTINUATIONS) via a fresh
      // bounded segment resumed from Mastra memory. Only once continuations
      // are exhausted do we fall back to the persisted STEP_LIMIT_NOTICE.
      const finishReason = getFinishReason(finalMessages);
      const shouldContinue =
        !isError &&
        finishReason === "tool-calls" &&
        continuationCountRef.current < MAX_AUTO_CONTINUATIONS;

      // Pick the notice for the just-finished segment. Skipped entirely when
      // we're about to auto-continue (the turn hasn't really ended). Abnormal
      // finish reasons other than tool-calls already carry a server-side
      // notice, and the error path has its own synthetic message.
      let outMessages = finalMessages;
      if (!isError && !shouldContinue) {
        const notice =
          finishReason === "tool-calls"
            ? STEP_LIMIT_NOTICE
            : isEmptyCompletion(finalMessages)
              ? "The agent finished without a reply. Try asking again."
              : undefined;
        if (notice) {
          outMessages = [
            ...finalMessages,
            makeSyntheticMessage("data-notice", notice),
          ];
          setMessages(outMessages);
        }
      }

      // Keep the shared cache current so an expand right after a turn shows the
      // latest transcript instantly on the full-page instance. This is a
      // session-only paint cache — the server (Mastra) is the source of truth
      // and is hydrated via `trpc.ai.messages` on reload.
      setCachedMessages(threadId, outMessages);

      if (shouldContinue) {
        continuationCountRef.current += 1;
        // Defer so `useChat` settles the finished segment before we kick off
        // the next request.
        queueMicrotask(() => {
          void sendMessage(
            {
              id: nanoid(),
              role: "user",
              parts: [{ type: "text", text: CONTINUATION_PROMPT }],
            } satisfies UIMessage,
            {
              body: {
                threadId: activeThreadId$.get(),
                requestContext: getChatContext(),
              },
            },
          );
        });
        return;
      }

      void refreshThreadTitle(threadId);
    },
  });

  const isBusy = status === "submitted" || status === "streaming";
  const hasMessages = messages.length > 0;

  // Mirrors `isBusy` into a ref so async callbacks (hydration) can read the
  // latest streaming state without being recreated on every status change.
  const isBusyRef = useRef(isBusy);
  useEffect(() => {
    isBusyRef.current = isBusy;
    // Publish streaming state globally so surfaces outside this component (the
    // full-page collapse button) can avoid navigating away mid-stream.
    chat$.isStreaming.set(isBusy);
  }, [isBusy]);

  // Ensure the global flag can't get stuck "streaming" if this instance
  // unmounts mid-turn (e.g. an unexpected navigation).
  useEffect(
    () => () => {
      chat$.isStreaming.set(false);
    },
    [],
  );

  // Expose this instance's abort to out-of-tree surfaces (e.g. the sidebar
  // deleting the streaming thread) so they can stop the stream cleanly.
  useEffect(() => {
    registerStop(stop);
    return () => registerStop(null);
  }, [stop]);

  // Monotonic counter so a slow, stale `hydrateThread` response can't clobber
  // a newer one (or freshly streamed messages).
  const hydrateSeqRef = useRef(0);

  // True when the last hydrate attempt failed (server error, not an empty
  // thread), so the pane can show a retryable error instead of the empty state.
  const [hydrateFailed, setHydrateFailed] = useState(false);

  // The thread id currently being hydrated from the server with no cached
  // transcript to paint from — drives the loading skeleton so a reloaded thread
  // doesn't flash the empty/suggested-prompts state before its messages land.
  // Seeded on first render so the very first paint already knows it's loading;
  // threads with a cached transcript (including locally created new threads,
  // which `newThread` seeds with `[]`) start non-hydrating.
  const [hydratingId, setHydratingId] = useState<string | null>(() => {
    const id = threadId ?? activeThreadId$.get();
    if (!id) return null;
    return getCachedMessages(id) ? null : id;
  });

  // Tracks the specific Error instance we already surfaced so re-renders (or
  // React Strict Mode double-invocations) don't append a second notice. A new
  // error is a new Error object, so identity comparison naturally resets.
  const handledErrorRef = useRef<Error | null>(null);

  // Surface supervisor stream errors as a synthetic assistant message with a
  // `data-error` part so the failure renders inline at the position where it
  // occurred, then clearError() so only the synthetic message drives the UI.
  // These notices are session-only: Mastra owns persistence and never writes
  // them back, so they disappear on reload alongside the partial turn.
  useEffect(() => {
    if (!error) {
      handledErrorRef.current = null;
      return;
    }
    if (handledErrorRef.current === error) return;
    handledErrorRef.current = error;

    if (error.name === "AbortError") {
      // Aborts are partial; surface a local-only "Stopped" notice.
      setMessages([
        ...messages,
        makeSyntheticMessage("data-notice", "Stopped by user."),
      ]);
    } else {
      const message = error.message?.trim() || "Something went wrong";
      setMessages([...messages, makeSyntheticMessage("data-error", message)]);
    }
    clearError();
  }, [error, messages, setMessages, clearError]);

  const hydrateThread = useCallback(
    async (id: string, options?: { clearOnError?: boolean }) => {
      // Never overwrite an in-flight stream's messages with a server snapshot.
      if (isBusyRef.current) return;

      // Paint any cached transcript immediately (before the network round-trip)
      // so a thread switch shows content instantly; the fetch below reconciles.
      // Only threads with no cache to paint from show the loading skeleton.
      const cached = getCachedMessages(id);
      if (cached) {
        setMessages(cached);
        setHydratingId(null);
      } else {
        setHydratingId(id);
      }

      const seq = ++hydrateSeqRef.current;
      try {
        const msgs = await trpc.ai.messages.query({ threadId: id });
        // Bail if a newer hydrate (or a new stream) superseded this request.
        if (seq !== hydrateSeqRef.current) return;
        setMessages(msgs);
        setCachedMessages(id, msgs);
        setHydrateFailed(false);
        setHydratingId(null);
      } catch {
        if (seq !== hydrateSeqRef.current) return;
        setHydrateFailed(true);
        setHydratingId(null);
        // Only wipe the transcript when intentionally switching threads; a
        // transient failure during mount/refresh must not blank existing
        // content (which would reappear on the next reload — the very bug
        // we're fixing).
        if (options?.clearOnError) setMessages([]);
      }
    },
    [setMessages],
  );

  const handleRetryHydrate = useCallback(() => {
    const id = activeThreadId$.get();
    if (id) void hydrateThread(id, { clearOnError: true });
  }, [hydrateThread]);

  // Threads whose background run we've already watched to completion this
  // session. Once finished, we never re-enter the read-only generating view
  // for them even though the `onboarding` metadata flag persists.
  const [finishedGenerating, setFinishedGenerating] = useState<Set<string>>(
    () => new Set(),
  );

  const activeId = useValue(activeThreadId$);

  // A thread is "generating" when it carries onboarding metadata, was created
  // recently, and we haven't already watched its run finish. Computed inside
  // `useValue` so it only re-renders when the boolean flips.
  const isGenerating = useValue(() => {
    const id = activeThreadId$.get();
    if (!id) return false;
    const meta = threads$[id].metadata.get() as
      | { onboarding?: boolean }
      | undefined;
    if (meta?.onboarding !== true) return false;
    return isRecent(threads$[id].createdAt.get());
  });

  // The durable run id persisted on the onboarding thread's metadata, used to
  // `observe` the live stream. May be null briefly before the server's
  // `updateThread` broadcast lands (or for an already-finished run); the
  // generating view handles null by falling through to the transcript.
  const generatingRunId = useValue(() => {
    const id = activeThreadId$.get();
    if (!id) return null;
    const meta = threads$[id].metadata.get() as { runId?: string } | undefined;
    return meta?.runId ?? null;
  });

  const handleGeneratingDone = useCallback(
    (id: string, hadError: boolean) => {
      setFinishedGenerating((prev) => {
        if (prev.has(id)) return prev;
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      // Pull the now-final transcript into the interactive `useChat` state so
      // the user can keep chatting from where the background run left off. When
      // the live tail dropped on an error, append a notice after hydrating so
      // the user understands why the live view disappeared.
      void hydrateThread(id).then(() => {
        if (!hadError) return;
        setMessages((prev) => [
          ...prev,
          makeSyntheticMessage(
            "data-notice",
            "Lost connection to the live run — showing the saved conversation.",
          ),
        ]);
      });
    },
    [hydrateThread, setMessages],
  );

  const showGenerating =
    isGenerating && !!activeId && !finishedGenerating.has(activeId);

  const handleSelectThread = useCallback(
    (id: string) => {
      activeThreadId$.set(id);
      void hydrateThread(id, { clearOnError: true });
    },
    [hydrateThread],
  );

  // On the first thread load that includes a recently created onboarding
  // thread (i.e. the user just came back from checkout), open the chat and
  // select the most recent one so they land on a live-tailing run. One-shot.
  const didAutoSelectRef = useRef(false);
  useObserve(threads$, () => {
    if (didAutoSelectRef.current) return;
    const map = threads$.get();
    if (!map) return;
    const onboarding = Object.values(map).filter(
      (t) =>
        (t.metadata as { onboarding?: boolean } | undefined)?.onboarding ===
          true && isRecent(t.createdAt),
    );
    if (onboarding.length === 0) return;
    didAutoSelectRef.current = true;
    const mostRecent = onboarding.reduce((a, b) =>
      new Date(b.createdAt).getTime() > new Date(a.createdAt).getTime() ? b : a,
    );
    queueMicrotask(() => {
      chat$.isOpen.set(true);
      activeThreadId$.set(mostRecent.id);
    });
  });

  const handleNewChat = useCallback(() => {
    setMessages([]);
    newThread();
    queueMicrotask(() => editorRef.current?.focus());
  }, [setMessages]);

  // Hydrate whenever the active thread ID resolves or changes. The ID starts
  // null and is set asynchronously once the thread list loads (and is reset on
  // org switch), so a mount-only effect would miss it and leave the panel
  // blank until the user manually switched threads. The seq/busy guards inside
  // `hydrateThread` keep this safe against stale responses and active streams.
  useObserve(activeThreadId$, ({ value }) => {
    if (value) queueMicrotask(() => void hydrateThread(value));
  });

  useObserve(chat$.isOpen, () => {
    if (chat$.isOpen.get()) {
      queueMicrotask(() => editorRef.current?.focus());
    }
  });

  const sendWithContext = useCallback(
    (textOrParts: string | UIMessage["parts"], files?: FileUIPart[]) => {
      // Fresh user turn: reset the auto-continuation budget.
      continuationCountRef.current = 0;
      const body = {
        threadId: activeThreadId$.get(),
        requestContext: getChatContext(),
      };

      // String overload: used by `SuggestedPrompts` and other callers that
      // only emit plain text. Falls through to the AI SDK convenience
      // `{ text, files }` shape so we don't churn behavior for them.
      if (typeof textOrParts === "string") {
        sendMessage(
          {
            text: textOrParts,
            ...(files && files.length > 0 && { files }),
          },
          { body },
        );
        return;
      }

      // Parts overload: used by the chat input. Files are prepended so any
      // attached images appear before the text/mention flow in the bubble.
      const parts: UIMessage["parts"] = [];
      if (files && files.length > 0) parts.push(...files);
      parts.push(...textOrParts);

      sendMessage(
        {
          id: nanoid(),
          role: "user",
          parts,
        } satisfies UIMessage,
        { body },
      );
    },
    [sendMessage],
  );

  const handleToolResume = useCallback(
    async (approval: ToolCallApprovalData, resumeData: ToolResumeData) => {
      try {
        await sendMessage(undefined, {
          body: {
            threadId: activeThreadId$.get(),
            requestContext: getChatContext(),
            resumeData,
            runId: approval.runId,
          },
        });
      } catch (err) {
        // A resume can reject before `useChat` ever sets `error` (e.g. the
        // workflow snapshot was evicted), which would otherwise surface as an
        // unhandled rejection and a form that appears frozen. Surface a
        // session-only notice (Mastra owns persistence, so it isn't written
        // back and clears on reload).
        logger.error({ err }, "Failed to resume tool call");
        setMessages([
          ...messages,
          makeSyntheticMessage(
            "data-error",
            "Couldn't resume — the request may have expired. Try asking again.",
          ),
        ]);
      }
    },
    [sendMessage, messages, setMessages],
  );

  const handleToolApproval = useCallback(
    (approval: ToolCallApprovalData, approved: boolean) =>
      handleToolResume(approval, { approved }),
    [handleToolResume],
  );

  // Approve a plan presented in Plan mode: drop back to Auto so the execution
  // turn runs with the full tool set, then send a canned message that kicks off
  // the build. Deferred to a microtask so the mode change lands before the send
  // reads it via `getChatContext()`.
  const handleApprovePlan = useCallback(() => {
    chat$.mode.set("AUTO");
    queueMicrotask(() => sendWithContext("Approved — execute the plan."));
  }, [sendWithContext]);

  // Re-run the last turn after a failure. Targeting the last user message's id
  // makes `regenerate` drop everything after it (any partial assistant turn
  // and the synthetic error message) before re-requesting, so we get a clean
  // retry without duplicating the user's message.
  const handleRetry = useCallback(() => {
    const lastUser = [...messages].reverse().find((m) => m.role === "user");
    if (!lastUser) return;
    // Retrying re-runs the turn from scratch, so restore the full budget.
    continuationCountRef.current = 0;
    void regenerate({
      messageId: lastUser.id,
      body: {
        threadId: activeThreadId$.get(),
        requestContext: getChatContext(),
      },
    }).catch((err) => logger.error({ err }, "Failed to retry chat message"));
  }, [messages, regenerate]);

  useObserve(chat$.pendingAiPrompt, () => {
    const prompt = chat$.pendingAiPrompt.get();
    if (!prompt) return;
    queueMicrotask(() => {
      sendWithContext(prompt);
      chat$.pendingAiPrompt.set(null);
    });
  });

  const handleSend = useCallback(() => {
    const editorParts = editorRef.current?.getMessageParts() ?? [];
    const readyImages = images.pendingImages.filter(
      (i) => i.status === "ready",
    );

    if (editorParts.length === 0 && readyImages.length === 0) return;

    const files: FileUIPart[] = readyImages
      .filter(
        (img): img is PendingImage & { dataUrl: string } =>
          img.dataUrl !== null,
      )
      .map((img) => ({
        type: "file" as const,
        mediaType: img.mediaType,
        url: img.dataUrl,
      }));

    sendWithContext(editorParts, files.length > 0 ? files : undefined);
    editorRef.current?.clear();
    setDraft(EMPTY_DRAFT);
    images.clearImages();
    // Keep focus on the editor for an immediate follow-up. Defer to after
    // React commits the post-send state so nothing steals focus back.
    queueMicrotask(() => editorRef.current?.focus());
  }, [images.pendingImages, sendWithContext, images.clearImages]);

  const handlePasteFiles = useCallback(
    (files: File[]) => {
      const imageFiles = files.filter((f) => f.type.startsWith("image/"));
      if (imageFiles.length > 0) images.addImages(imageFiles);
    },
    [images],
  );

  const hasContent = draft.text.trim().length > 0 || draft.mentionCount > 0;
  const isPage = layout === "page";

  // True while the active thread's transcript is still loading from the server
  // with nothing cached to paint. Cached paints and streamed messages both give
  // `hasMessages`, which suppresses the skeleton.
  const isHydrating =
    hydratingId !== null && hydratingId === activeId && !hasMessages;

  // On the full-page thread view, an empty conversation centers the input (with
  // its suggested prompts) in the content area rather than docking it to the
  // bottom, for a more polished first-message experience. Excludes the loading
  // and hydrate-failure states so a reloaded thread doesn't flash the centered
  // empty layout before its messages (or a retry prompt) resolve.
  const showCenteredEmptyState =
    isPage && !hasMessages && !showGenerating && !isHydrating && !hydrateFailed;

  // On the full-page layout the scroll container (`Conversation`) spans the
  // whole content pane so the wheel/trackpad works over the side gutters, while
  // the messages stay in a centered reading column via `contentClassName`.
  // Non-scrolling states center themselves so they line up with that column.
  // Full-page reading column. `py-6` gives the transcript breathing room at
  // the top (below the page header) and bottom (above the docked input) so the
  // conversation doesn't feel cramped in the wider full-page layout.
  const pageContentClassName = isPage
    ? "mx-auto w-full max-w-3xl py-6"
    : undefined;
  const conversation = (
    <div className="no-scrollbar min-h-0 flex-1 overflow-hidden">
      {showGenerating && activeId ? (
        <GeneratingThreadView
          key={activeId}
          threadId={activeId}
          runId={generatingRunId}
          onDone={handleGeneratingDone}
          contentClassName={pageContentClassName}
        />
      ) : hydrateFailed && !hasMessages ? (
        <div
          className={cn(
            "flex size-full flex-col items-center justify-center gap-2 text-center",
            isPage && "mx-auto max-w-3xl",
          )}
        >
          <p className="text-muted-foreground text-xs">
            Couldn't load this conversation.
          </p>
          <Button size="sm" variant="outline" onClick={handleRetryHydrate}>
            Retry
          </Button>
        </div>
      ) : isHydrating ? (
        isPage ? (
          <div className="mx-auto h-full w-full max-w-3xl">
            <ChatMessagesSkeleton />
          </div>
        ) : (
          <ChatMessagesSkeleton />
        )
      ) : !hasMessages ? (
        <div
          className={cn(
            "flex size-full flex-col justify-end",
            isPage && "mx-auto max-w-3xl",
          )}
        >
          <SuggestedPrompts onSend={sendWithContext} />
        </div>
      ) : (
        <ChatMessages
          messages={messages}
          isBusy={isBusy}
          onToolApproval={handleToolApproval}
          onToolResume={handleToolResume}
          onRetry={handleRetry}
          onApprovePlan={handleApprovePlan}
          contentClassName={pageContentClassName}
        />
      )}
    </div>
  );

  const input = (
    <div className="shrink-0 p-2 pt-0">
      <ChatInput
        hasContent={hasContent}
        onChange={setDraft}
        onSend={handleSend}
        onStop={stop}
        onAttachClick={() => fileInputRef.current?.click()}
        onFileInputChange={images.handleFileInputChange}
        onPasteFiles={handlePasteFiles}
        pendingImages={images.pendingImages}
        onRemoveImage={images.removeImage}
        isBusy={isBusy || showGenerating}
        isProcessingImages={images.isProcessingImages}
        editorRef={editorRef}
        fileInputRef={fileInputRef}
      />
    </div>
  );

  if (isPage) {
    // Full-page route: the page renders its own header, so the conversation
    // and input are centered in a fixed reading width like boards/datasets.
    //
    // The outer column is full-width so the message scroll container catches
    // wheel/trackpad events over the side gutters; the reading-width cap lives
    // on the inner content (`pageContentClassName`) and on the input wrapper.
    return (
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex h-full min-h-0 w-full flex-col">
          {showCenteredEmptyState ? (
            <div className="mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col justify-center">
              <SuggestedPrompts onSend={sendWithContext} />
              {input}
            </div>
          ) : (
            <>
              {conversation}
              <div className="mx-auto w-full max-w-3xl pb-4">{input}</div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    // On mobile the chat is a full-screen sheet flush with the top of the
    // viewport, so add breathing room (and clear any device notch) above the
    // thread-name header. Desktop docks below the app top bar and needs none.
    <div className="flex h-full min-h-0 flex-col pt-[max(0.5rem,env(safe-area-inset-top))] md:pt-0">
      <ChatHeader
        isBusy={isBusy}
        hasMessages={hasMessages}
        onNewChat={handleNewChat}
        onSelectThread={handleSelectThread}
        showExpand
      />
      {conversation}
      {input}
    </div>
  );
};
